import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

/**
 * Error Interceptor
 *
 * Catches and handles HTTP errors from API requests:
 * - 4xx Client Errors (400, 401, 403, 404, etc.)
 * - 5xx Server Errors (500, 502, 503, 504, etc.)
 * - Logs all errors to console with detailed information
 * - Special handling: Redirects to /404 page for 404 errors
 *
 * Execution order: Last (runs after all other interceptors)
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Prepare error log data
      const errorLog = {
        timestamp: new Date(),
        status: error.status,
        message: getErrorMessage(error),
        url: req.url,
        error: error.error
      };

      // Log error to console with formatted output
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('🚨 HTTP Error Intercepted');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error(`Timestamp: ${errorLog.timestamp.toISOString()}`);
      console.error(`Status: ${errorLog.status}`);
      console.error(`URL: ${errorLog.url}`);
      console.error(`Message: ${errorLog.message}`);
      console.error('Error Details:', errorLog.error);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Special handling for 404 errors - redirect to 404 page
      if (error.status === 404) {
        router.navigate(['/404'], {
          skipLocationChange: false,
          state: { requestedUrl: req.url }
        });
      }

      // Re-throw the error so components can handle it if needed
      return throwError(() => error);
    })
  );
};

/**
 * Extracts a user-friendly error message from the HttpErrorResponse
 */
function getErrorMessage(error: HttpErrorResponse): string {
  if (error.error instanceof ErrorEvent) {
    // Client-side or network error
    return `Network error: ${error.error.message}`;
  }

  // Server-side error
  switch (error.status) {
    case 400:
      return 'Bad Request - The server could not understand the request.';
    case 401:
      return 'Unauthorized - Authentication is required.';
    case 403:
      return 'Forbidden - You do not have permission to access this resource.';
    case 404:
      return 'Not Found - The requested resource could not be found.';
    case 408:
      return 'Request Timeout - The server timed out waiting for the request.';
    case 429:
      return 'Too Many Requests - Rate limit exceeded.';
    case 500:
      return 'Internal Server Error - The server encountered an error.';
    case 502:
      return 'Bad Gateway - Invalid response from the upstream server.';
    case 503:
      return 'Service Unavailable - The server is temporarily unavailable.';
    case 504:
      return 'Gateway Timeout - The upstream server timed out.';
    default:
      if (error.status >= 400 && error.status < 500) {
        return `Client Error (${error.status}) - Request failed`;
      } else if (error.status >= 500) {
        return `Server Error (${error.status}) - Server failed to process request`;
      }
      return error.message || 'An unknown error occurred';
  }
}
